import { z } from 'zod';

import {
  BillingCycleSchema,
  FeatureKeySchema,
  FeatureMapSchema,
  CategorySchema,
  NeighborhoodSchema,
  PlanSchema,
  PnlMonthSchema,
  RoleSchema,
} from '../../api/schemas';

/** Response contracts for the platform-admin panel API (services/api: admin-auth-routes.ts, admin-routes.ts). */

export const AdminLoginResponseSchema = z.object({
  sessionTransport: z.enum(['cookie', 'bearer']),
  sessionToken: z.string().optional(),
  adminId: z.string(),
  username: z.string(),
  expiresAt: z.string(),
  mustChangePassword: z.boolean(),
});
export type AdminLoginResponse = z.infer<typeof AdminLoginResponseSchema>;

// ---------------------------------------------------------------------------
// Negocios (businesses list + coverage status)
// ---------------------------------------------------------------------------

export const BusinessCoverageStatusSchema = z.enum(['al_dia', 'atrasado']);
export type BusinessCoverageStatus = z.infer<typeof BusinessCoverageStatusSchema>;

export const AdminBusinessSchema = z.object({
  id: z.string(),
  name: z.string(),
  category: z.string(),
  neighborhood: z.string(),
  isActive: z.boolean(),
  /** Cross-link into /admin/organizaciones; null on a business predating 0021 backfill. */
  organizationId: z.string().nullable().optional(),
  /** "Cerrado temporalmente", the owner's own switch. */
  isPaused: z.boolean().optional(),
  /** When the platform suspended it; null while it is running. */
  suspendedAt: z.string().nullable().optional(),
  /** Signed up on its own and nobody has approved it yet. */
  pendingApproval: z.boolean().optional(),
  lastCoveredMonth: z.string().nullable(),
  status: BusinessCoverageStatusSchema,
  /** Chosen by an admin or the owner; absent on an older payload. */
  plan: PlanSchema.optional(),
  monthlyPriceCents: z.number().optional(),
  /** Last plan change; null when the plan was set at onboarding and never moved. */
  planChangedAt: z.string().nullable().optional(),
  /** Month by month or the year up front; absent on an older payload, which was monthly. */
  billingCycle: BillingCycleSchema.optional(),
  /** The free trial's end; null when there is none, absent on an older payload. */
  trialEndsAt: z.string().nullable().optional(),
  /**
   * Versiones contra choques (0099): the version of the plan, its price, the
   * cycle and the free trial together. Sent back as `expectedVersion` by the plan
   * and the trial forms; absent on an older payload.
   */
  planVersion: z.number().int().positive().optional(),
});
export type AdminBusiness = z.infer<typeof AdminBusinessSchema>;

/**
 * What a 409 `version_conflict` on the plan or the trial carries in `current`:
 * the roster entry. Only the terms `planVersion` guards are read from it, so a
 * conflict is understood even when the rest of the entry is partial.
 */
export const AdminPlanTermsSchema = AdminBusinessSchema.pick({
  plan: true,
  monthlyPriceCents: true,
  billingCycle: true,
  planChangedAt: true,
  trialEndsAt: true,
  planVersion: true,
});
export type AdminPlanTerms = z.infer<typeof AdminPlanTermsSchema>;

export const AdminBusinessListResponseSchema = z.object({ data: z.array(AdminBusinessSchema) });

/** `PUT /v1/admin/businesses/:businessId/trial`. */
export const AdminTrialSetResponseSchema = z.object({
  data: z.object({
    businessId: z.string(),
    trialEndsAt: z.string().nullable(),
    /** The plan version after the save (the trial is one of the terms it covers). */
    planVersion: z.number().int().positive().optional(),
  }),
});

/** One move on a business's plan ladder, by the platform or by the owner. */
export const AdminPlanChangeSchema = z.object({
  id: z.string(),
  fromPlan: PlanSchema.nullable(),
  toPlan: PlanSchema,
  fromPriceCents: z.number().nullable(),
  toPriceCents: z.number(),
  /** Absent on a row from before annual billing, which was monthly. */
  fromBillingCycle: BillingCycleSchema.optional(),
  toBillingCycle: BillingCycleSchema.optional(),
  actorKind: z.enum(['admin', 'owner']),
  note: z.string().nullable().optional(),
  createdAt: z.string(),
});
export type AdminPlanChange = z.infer<typeof AdminPlanChangeSchema>;
export const AdminPlanChangeListResponseSchema = z.object({ data: z.array(AdminPlanChangeSchema) });
export const AdminPlanSetResponseSchema = z.object({
  data: z
    .object({
      plan: PlanSchema,
      monthlyPriceCents: z.number(),
      billingCycle: BillingCycleSchema.optional(),
      planChangedAt: z.string().nullable().optional(),
      /** The plan version after the save. */
      planVersion: z.number().int().positive().optional(),
    })
    .passthrough(),
});

// ---------------------------------------------------------------------------
// Pagos
// ---------------------------------------------------------------------------

/** 'tarjeta' and 'paypal' arrive from the owner's own checkout (/pago); 'stripe' is a paid Stripe invoice. */
export const PaymentMethodSchema = z.enum(['yappy', 'efectivo', 'transferencia', 'tarjeta', 'paypal', 'stripe', 'apple']);
export type PaymentMethod = z.infer<typeof PaymentMethodSchema>;
/** What an admin records by hand; card and PayPal only ever come from /pago. */
export type RecordablePaymentMethod = Extract<PaymentMethod, 'yappy' | 'efectivo' | 'transferencia'>;

export const PaymentStatusSchema = z.enum(['pendiente', 'reportado', 'confirmado', 'fallido', 'rechazado']);
export type PaymentStatus = z.infer<typeof PaymentStatusSchema>;

export const AdminPaymentSchema = z.object({
  id: z.string(),
  businessId: z.string(),
  amountCents: z.number(),
  method: PaymentMethodSchema,
  paidOn: z.string(),
  periodMonth: z.string(),
  note: z.string().nullable().optional(),
  /** Null on an owner's own checkout, which no admin recorded. */
  createdBy: z.string().nullable(),
  createdAt: z.string(),
  // The checkout (migration 0068). Absent on a row from before it: confirmed money for one month.
  status: PaymentStatusSchema.optional(),
  monthsCovered: z.number().int().optional(),
  plan: z.enum(['reservas', 'base', 'pro', 'max', 'ultra']).nullable().optional(),
  billingCycle: z.enum(['monthly', 'annual']).nullable().optional(),
  source: z.enum(['admin', 'owner']).optional(),
  /** The operation number an owner reported, or the provider's order or link. */
  providerRef: z.string().nullable().optional(),
  statusNote: z.string().nullable().optional(),
  decidedAt: z.string().nullable().optional(),
  /** Versiones contra choques (0099): sent back as `expectedVersion` when the payment is deleted. */
  version: z.number().int().positive().optional(),
});
export type AdminPayment = z.infer<typeof AdminPaymentSchema>;

export const AdminPaymentListResponseSchema = z.object({ data: z.array(AdminPaymentSchema) });

/** The create route answers with the same `{ data }` envelope the list does. */
export const AdminPaymentResponseSchema = z.object({ data: AdminPaymentSchema });

// ---------------------------------------------------------------------------
// Accesos (business-user rows on the negocio detail page)
// ---------------------------------------------------------------------------

export const AdminBusinessUserSchema = z.object({
  id: z.string(),
  name: z.string(),
  /** Nullable like the business portal's own record: the store hands back null. */
  email: z.string().optional().nullable(),
  /** Null on a row provisioned before usernames existed. */
  username: z.string().optional().nullable(),
  role: RoleSchema,
  isActive: z.boolean(),
  mustChangePassword: z.boolean(),
  locked: z.boolean(),
  /** Versiones contra choques (0099): sent back as `expectedVersion` when the username changes. */
  version: z.number().int().positive().optional(),
});
export type AdminBusinessUser = z.infer<typeof AdminBusinessUserSchema>;

export const AdminBusinessUserListResponseSchema = z.object({ data: z.array(AdminBusinessUserSchema) });

export const AdminResetPasswordResponseSchema = z.object({ temporaryPassword: z.string() });

export const AdminUsernameResponseSchema = z.object({
  username: z.string(),
  /** The access's version after the save. */
  version: z.number().int().positive().optional(),
});

// ---------------------------------------------------------------------------
// Organizaciones (multi-local onboarding; services/api admin-routes.ts)
// ---------------------------------------------------------------------------

export const AdminOrganizationSchema = z.object({
  id: z.string(),
  name: z.string(),
  businessCount: z.number(),
});
export type AdminOrganization = z.infer<typeof AdminOrganizationSchema>;

export const AdminOrganizationListResponseSchema = z.object({ data: z.array(AdminOrganizationSchema) });
export const AdminOrganizationResponseSchema = z.object({ data: AdminOrganizationSchema });

export const AdminOrganizationBusinessSchema = z.object({
  id: z.string(),
  name: z.string(),
  category: z.string(),
  neighborhood: z.string(),
  isActive: z.boolean(),
  /** Derived server-side from `monthlyPriceCents`; absent on an older payload. */
  plan: PlanSchema.optional(),
  monthlyPriceCents: z.number().optional(),
});
export type AdminOrganizationBusiness = z.infer<typeof AdminOrganizationBusinessSchema>;

export const AdminOrganizationOwnerSchema = z.object({
  id: z.string(),
  name: z.string(),
  username: z.string().optional().nullable(),
  email: z.string().optional().nullable(),
});
export type AdminOrganizationOwner = z.infer<typeof AdminOrganizationOwnerSchema>;

export const AdminOrganizationDetailSchema = z.object({
  id: z.string(),
  name: z.string(),
  businesses: z.array(AdminOrganizationBusinessSchema),
  owners: z.array(AdminOrganizationOwnerSchema),
});
export type AdminOrganizationDetail = z.infer<typeof AdminOrganizationDetailSchema>;

export const AdminOrganizationDetailResponseSchema = z.object({ data: AdminOrganizationDetailSchema });

/** The onboarding form's category/zona vocabulary: the same one a real Negocio profile validates against. */
export const ORGANIZATION_CATEGORY_OPTIONS = CategorySchema.options;
export const ORGANIZATION_NEIGHBORHOOD_OPTIONS = NeighborhoodSchema.options;

/** Shared by the create POST and the reprice PATCH: both answer one full record. */
export const AdminOrganizationBusinessCreateResponseSchema = z.object({
  data: AdminOrganizationBusinessSchema.extend({
    timezone: z.string(),
    emoji: z.string(),
  }),
});

export const AdminOrganizationOwnerCreateResponseSchema = z.object({
  data: z.object({
    userId: z.string(),
    username: z.string(),
    temporaryPassword: z.string(),
  }),
});

// ---------------------------------------------------------------------------
// Finanzas de la plataforma (services/api: admin-finance-routes.ts)
// ---------------------------------------------------------------------------

export const PlatformExpenseCategorySchema = z.enum([
  'hosting',
  'dominios',
  'servicios',
  'marketing',
  'otro',
]);
export type PlatformExpenseCategory = z.infer<typeof PlatformExpenseCategorySchema>;

export const AdminExpenseSchema = z.object({
  id: z.string(),
  concept: z.string(),
  amountCents: z.number(),
  category: PlatformExpenseCategorySchema,
  incurredOn: z.string(),
  isRecurringMonthly: z.boolean(),
  recurringUntil: z.string().nullable(),
  isActive: z.boolean(),
  createdBy: z.string(),
  /** Versiones contra choques (0099): sent back as `expectedVersion` on Terminar recurrencia and Eliminar. */
  version: z.number().int().positive().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type AdminExpense = z.infer<typeof AdminExpenseSchema>;

export const AdminExpenseListResponseSchema = z.object({ data: z.array(AdminExpenseSchema) });
export const AdminExpenseResponseSchema = z.object({ data: AdminExpenseSchema });

export const AdminPnlResponseSchema = z.object({
  data: z.object({
    from: z.string(),
    to: z.string(),
    revenueCents: z.number(),
    expenseCents: z.number(),
    netCents: z.number(),
    // partialRecord, not record: only the categories that actually spent are
    // present, and a plain z.record over an enum key demands every one.
    expensesByCategory: z.partialRecord(PlatformExpenseCategorySchema, z.number()),
    months: z.array(PnlMonthSchema),
  }),
});
export type AdminPnl = z.infer<typeof AdminPnlResponseSchema>['data'];

// ---------------------------------------------------------------------------
// Funciones por negocio (GET/PUT /v1/admin/businesses/:businessId/features)
// ---------------------------------------------------------------------------

export const AdminFeaturesSchema = z.object({
  businessId: z.string(),
  plan: PlanSchema,
  /** The switches the list shows, in order; a key this panel does not know is left out. */
  keys: z.array(z.string()).transform((keys) => keys.filter((key) => FeatureKeySchema.safeParse(key).success) as z.infer<typeof FeatureKeySchema>[]),
  /** Only what an operator set: true on, false off. Absent keys follow the plan. */
  overrides: FeatureMapSchema,
  /** What the business gets. */
  effective: FeatureMapSchema,
});
export type AdminFeatures = z.infer<typeof AdminFeaturesSchema>;
export const AdminFeaturesResponseSchema = z.object({ data: AdminFeaturesSchema });

// ---------------------------------------------------------------------------
// Pagos que entraron (GET /v1/admin/payments/feed)
// ---------------------------------------------------------------------------

export const PaymentsFeedItemSchema = z.object({
  id: z.string(),
  businessId: z.string(),
  businessName: z.string(),
  amountCents: z.number(),
  method: PaymentMethodSchema,
  status: z.enum(['confirmado', 'reportado']),
  plan: PlanSchema.nullable(),
  at: z.string(),
});
export type PaymentsFeedItem = z.infer<typeof PaymentsFeedItemSchema>;

export const PaymentsFeedSchema = z.object({
  items: z.array(PaymentsFeedItemSchema),
  totals: z.object({ todayCents: z.number(), todayCount: z.number(), monthCents: z.number(), monthCount: z.number() }),
});
export type PaymentsFeed = z.infer<typeof PaymentsFeedSchema>;
export const PaymentsFeedResponseSchema = z.object({ data: PaymentsFeedSchema });

// ---------------------------------------------------------------------------
// Dale HQ (services/api: admin-hq-routes.ts)
// ---------------------------------------------------------------------------

/** One platform admin of the Equipo card: public fields only, never a credential. */
export const HqTeamMemberSchema = z.object({
  id: z.string(),
  username: z.string(),
  isActive: z.boolean(),
  /** When the account was provisioned; null for a record that never carried it. */
  createdAt: z.string().nullable(),
  /** The newest sign in; null while nobody has used the account. */
  lastLoginAt: z.string().nullable(),
});
export type HqTeamMember = z.infer<typeof HqTeamMemberSchema>;
export const HqTeamResponseSchema = z.object({ data: z.array(HqTeamMemberSchema) });

/** How much one business used Dale in a month: counts, never a person. */
export const HqActivityBusinessSchema = z.object({
  businessId: z.string(),
  bookings: z.number(),
  charges: z.object({ count: z.number(), cents: z.number() }),
  customers: z.number(),
  /** Null while the business has no agent conversation or usage reading in the month. */
  agents: z.object({ conversations: z.number(), costCents: z.number() }).nullable(),
});
export type HqActivityBusiness = z.infer<typeof HqActivityBusinessSchema>;
export const HqActivityResponseSchema = z.object({
  data: z.object({ month: z.string(), businesses: z.array(HqActivityBusinessSchema) }),
});
export type HqActivityReport = z.infer<typeof HqActivityResponseSchema>['data'];

/** Dale AI's answer. */
export const HqAiResponseSchema = z.object({ data: z.object({ answer: z.string() }) });
